import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ESTIMATING VIEW ADDITIONAL WORKS PAGE
 *
 * Single-page quote view for Additional Works quotes. Combined layout
 * (no pagination), with "Additional Works" label, Reference Job Number,
 * and without Project Overview, Exclusions, or Compliance & Safety.
 */
import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { quotesService, } from "@/api/services/quotes.service";
import { contactsService } from "@/api/services/contacts.service";
import { jobsService } from "@/api/services/jobs.service";
import { getContactDisplayName } from "@/utils/string";
import { matchesQuoteStatus, getQuoteStatusForDisplay, } from "@/utils/quoteHelpers";
import { useAlert } from "@/hooks/useAlert";
import { QuoteAdditionalWorksReportContent } from "./components/QuoteAdditionalWorksReportContent";
import styles from "./EstimatingViewSite.module.css";
const getStatusLabel = (status) => {
    const labels = {
        draft: "Requested",
        inprogress: "In Progress",
        pending: "Pending Approval",
        approved: "Approved",
        declined: "Declined",
        cancelled: "Cancelled",
    };
    return labels[status] || status;
};
const BackIcon = () => (_jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M19 12H5M12 19l-7-7 7-7" }) }));
import { formatDateOnlyDisplay } from "@/utils/dateTime";
const formatDate = (dateString) => !dateString ? "[DD-MMM-YYYY]" : formatDateOnlyDisplay(dateString);
export const EstimatingViewAdditionalWorks = ({ initialQuote, }) => {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { alert, confirm } = useAlert();
    const [quote, setQuote] = useState(initialQuote);
    const [sendingQuote, setSendingQuote] = useState(false);
    const [fetchedAttentionName, setFetchedAttentionName] = useState(null);
    const [fetchedCompanyName, setFetchedCompanyName] = useState(null);
    const [referenceJobNumber, setReferenceJobNumber] = useState(null);
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    useEffect(() => {
        setQuote(initialQuote);
    }, [initialQuote]);
    const fetchQuote = useCallback(async () => {
        if (!quoteId)
            return;
        try {
            const response = await quotesService.getQuote(quoteId);
            setQuote(response.data);
        }
        catch (err) {
            console.error("Error fetching quote:", err);
        }
    }, [quoteId]);
    useEffect(() => {
        if (quote?.mainJobId) {
            jobsService
                .getJob(quote.mainJobId)
                .then((res) => setReferenceJobNumber(res.data.jobNumber ?? null))
                .catch(() => setReferenceJobNumber(null));
        }
        else {
            setReferenceJobNumber(null);
        }
    }, [quote?.mainJobId]);
    useEffect(() => {
        if (quote?.attentionContactId && !quote?.attentionContactName) {
            contactsService
                .getContact(quote.attentionContactId)
                .then((response) => {
                const contact = response.data;
                const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
                if (contact.companyName)
                    setFetchedCompanyName(contact.companyName);
                if (displayName)
                    setFetchedAttentionName(displayName);
            })
                .catch(() => { });
        }
        else {
            setFetchedAttentionName(null);
            setFetchedCompanyName(null);
        }
    }, [quote?.attentionContactId, quote?.attentionContactName]);
    const handleBack = () => {
        const previousPath = location.state?.from;
        if (previousPath)
            navigate(previousPath);
        else
            navigate(`${basePath}/estimating`);
    };
    const handleSendQuote = async () => {
        if (!quoteId || !quote)
            return;
        if (!matchesQuoteStatus(quote.status, "draft") &&
            !matchesQuoteStatus(quote.status, "inprogress")) {
            alert("This quote has already been sent to the client.");
            return;
        }
        const confirmSend = await confirm("", "Are you sure you want to send this quote to the client?", { confirmText: "Yes, I am sure", cancelText: "No, cancel" });
        if (!confirmSend)
            return;
        try {
            setSendingQuote(true);
            await quotesService.updateQuoteStatus(quoteId, "pending");
            await fetchQuote();
            await alert("Quote sent to client successfully!");
        }
        catch (err) {
            console.error("Error sending quote:", err);
            await alert("Failed to send quote. Please try again.");
        }
        finally {
            setSendingQuote(false);
        }
    };
    if (!quote) {
        return null;
    }
    return (_jsxs("div", { className: styles.page, children: [_jsxs("header", { className: styles.header, children: [_jsx("button", { onClick: handleBack, className: styles.backButton, children: _jsx(BackIcon, {}) }), _jsxs("h1", { className: styles.headerTitle, children: ["Project Quote #", quote.quoteNumber] })] }), _jsx("div", { className: styles.reportContainer, children: _jsxs("div", { className: styles.darkBackground, children: [_jsx("div", { className: styles.logoContainer, children: _jsx("img", { src: "/logo-white.png", alt: "The Defit Group", className: styles.logo }) }), _jsxs("div", { className: styles.topRightActions, children: [matchesQuoteStatus(quote.status, "draft") ||
                                    matchesQuoteStatus(quote.status, "inprogress") ? (_jsx("button", { onClick: handleSendQuote, className: styles.sendQuoteButton, disabled: sendingQuote, children: sendingQuote ? "Sending..." : "Send Quote" })) : (_jsx("div", { className: styles.statusBadge, "data-status": getQuoteStatusForDisplay(quote.status), children: getStatusLabel(getQuoteStatusForDisplay(quote.status)) })), _jsx("div", { className: styles.dateBadge, children: formatDate(quote.createdAt) })] }), _jsx("div", { className: styles.documentTitle, children: "Quote" }), _jsx(QuoteAdditionalWorksReportContent, { quote: quote, referenceJobNumber: referenceJobNumber, fetchedAttentionName: fetchedAttentionName, fetchedCompanyName: fetchedCompanyName })] }) })] }));
};
